import React, { useState, useEffect, useMemo } from 'react';
import { getCompetitionStatus, startCompetitionWithBestModels, startCompetition, stopCompetition, resetCompetition, getAvailableExperiments, previewModelSelection, startEnhancedCompetition } from '../../api/rlApi';
import './RLCompetition.css';

const RLCompetition = () => {
  const [competitionStatus, setCompetitionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [availableExperiments, setAvailableExperiments] = useState([]);
  const [bestExperimentId, setBestExperimentId] = useState('');
  const [modelPreview, setModelPreview] = useState(null);
  const [competitionConfig, setCompetitionConfig] = useState({
    experiment_id: null,
    num_models: 3,
    selection_criteria: 'sharpe_ratio',
    ensure_diversity: true,
    max_duration_hours: null,
    risk_multiplier: 1.0,
    allow_short_selling: false,
    initial_balance: 10000,
  });

  useEffect(() => {
    fetchCompetitionStatus();
    // Slow down polling to reduce load (15 minutes)
    const interval = setInterval(fetchCompetitionStatus, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Preload available experiments for dropdowns
    (async () => {
      try {
        const experiments = await getAvailableExperiments();
        setAvailableExperiments(experiments.experiments || []);
        if (!bestExperimentId && experiments.experiments?.length > 0) {
          setBestExperimentId(experiments.experiments[0].experiment_id);
        }
      } catch (err) {
        console.error('Failed to preload experiments:', err);
      }
    })();
  }, [bestExperimentId]);

  const withTimeout = (promise, label, ms = 15000) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Timed out loading ${label}`)), ms)
      ),
    ]);

  const fetchCompetitionStatus = async () => {
    try {
      const response = await withTimeout(getCompetitionStatus(), 'competition');
      setCompetitionStatus(response);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to fetch competition status');
      console.error('Competition status error:', err);
    }
  };

  const handleStartCompetitionWithBestModels = async (experimentId) => {
    setLoading(true);
    try {
      const response = await startCompetitionWithBestModels(experimentId);
      setCompetitionStatus(await getCompetitionStatus());
      alert('Competition started with best 3 models!');
    } catch (err) {
      setError('Failed to start competition');
      console.error('Start competition error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConfigModal = async () => {
    setLoading(true);
    try {
      const experiments = await getAvailableExperiments();
      setAvailableExperiments(experiments.experiments || []);
      if (!bestExperimentId && experiments.experiments?.length > 0) {
        setBestExperimentId(experiments.experiments[0].experiment_id);
      }
      setShowConfigModal(true);
    } catch (err) {
      setError('Failed to load available experiments');
      console.error('Load experiments error:', err);
    } finally {
      setLoading(false);
    }
  };

  const sanitizeConfig = (config) => {
    const sanitized = { ...config };
    // Ensure all string values are properly escaped and don't contain malformed escapes
    Object.keys(sanitized).forEach(key => {
      if (typeof sanitized[key] === 'string') {
        // Remove any incomplete hex escapes (like \x, \u followed by incomplete hex)
        sanitized[key] = sanitized[key]
          .replace(/\\x[0-9a-fA-F]{0,1}$/g, '') // Remove incomplete \x escapes at end
          .replace(/\\u[0-9a-fA-F]{0,3}$/g, '') // Remove incomplete \u escapes at end
          .replace(/\\x[^0-9a-fA-F]/g, '') // Remove invalid \x escapes
          .replace(/\\u[^0-9a-fA-F]/g, ''); // Remove invalid \u escapes
      }
    });
    return sanitized;
  };

  const handlePreviewModels = async () => {
    setLoading(true);
    try {
      const sanitizedConfig = sanitizeConfig(competitionConfig);
      const preview = await previewModelSelection(sanitizedConfig);
      setModelPreview(preview);
    } catch (err) {
      setError('Failed to preview model selection');
      console.error('Preview models error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEnhancedCompetition = async () => {
    setLoading(true);
    try {
      const sanitizedConfig = sanitizeConfig(competitionConfig);
      const response = await startEnhancedCompetition(sanitizedConfig);
      setCompetitionStatus(await getCompetitionStatus());
      setShowConfigModal(false);
      setModelPreview(null);
      alert(`Enhanced competition started with ${competitionConfig.num_models} models!`);
    } catch (err) {
      setError('Failed to start enhanced competition');
      console.error('Start enhanced competition error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartCompetition = async () => {
    setLoading(true);
    try {
      await startCompetition();
      await fetchCompetitionStatus();
    } catch (err) {
      setError('Failed to start competition');
      console.error('Start competition error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartBestModels = async () => {
    setLoading(true);
    try {
      let experimentId = bestExperimentId;
      if (!experimentId) {
        const experiments = await getAvailableExperiments();
        const first = experiments.experiments?.[0]?.experiment_id;
        if (!first) {
          alert('No experiments available to start best-models competition.');
          setLoading(false);
          return;
        }
        setAvailableExperiments(experiments.experiments || []);
        experimentId = first;
        setBestExperimentId(first);
      }
      await startCompetitionWithBestModels(experimentId);
      await fetchCompetitionStatus();
      alert('Competition started with best models from selected experiment.');
    } catch (err) {
      setError('Failed to start best-models competition');
      console.error('Start best-models error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStopCompetition = async () => {
    setLoading(true);
    try {
      await stopCompetition();
      await fetchCompetitionStatus();
    } catch (err) {
      setError('Failed to stop competition');
      console.error('Stop competition error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetCompetition = async () => {
    if (!confirm('Are you sure you want to reset the competition? This will clear all portfolios.')) {
      return;
    }
    setLoading(true);
    try {
      await resetCompetition();
      await fetchCompetitionStatus();
    } catch (err) {
      setError('Failed to reset competition');
      console.error('Reset competition error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatPercentage = (value) => {
    // Temporary fix: divide by 100 to correct the return percentage display
    // Backend is serving correct values but frontend may be showing cached data
    const correctedValue = value / 100;
    return `${(correctedValue * 100).toFixed(2)}%`;
  };

  const formatDrawdown = (drawdownPct, portfolioValue, initialBalance = 10000) => {
    // Drawdown from backend is decimal (like 0.0059), scale it like return % (multiply by 100)
    // to match the corrected return percentage scale
    const scaledDrawdownPct = drawdownPct * 100;
    const drawdownAmount = scaledDrawdownPct * initialBalance / 100;
    return `${scaledDrawdownPct.toFixed(2)}% ($${drawdownAmount.toFixed(2)})`;
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return '🥇';
      case 2:
        return '🥈';
      case 3:
        return '🥉';
      default:
        return `#${rank}`;
    }
  };

  const getStatusColor = (value, thresholds = { good: 0, warning: -5 }) => {
    if (value >= thresholds.good) return '#48bb78';
    if (value >= thresholds.warning) return '#ed8936';
    return '#f56565';
  };

  // Render base UI even if loading/error; show inline states

  const { status, leaderboard = [], agent_details = {}, total_agents = 0, active_agents = 0, duration_seconds, completed_competitions = [] } = competitionStatus || {};

  const displayLeaderboard = useMemo(() => (leaderboard || []).slice(0, 20), [leaderboard]);
  const agentEntries = useMemo(() => Object.entries(agent_details || {}).slice(0, 10), [agent_details]);
  const hasData =
    (total_agents > 0) ||
    (displayLeaderboard.length > 0) ||
    (agentEntries.length > 0) ||
    (completed_competitions && completed_competitions.length > 0);

  return (
    <div className="rl-competition">
      {error && (
        <div className="rl-competition-error">
          <h3>⚠️ Error Loading Competition</h3>
          <p>{error}</p>
          <button onClick={fetchCompetitionStatus} className="rl-retry-btn">
            Retry
          </button>
        </div>
      )}
      {!competitionStatus && !error && (
        <div className="rl-competition-loading">
          <div className="rl-spinner"></div>
          <p>Loading competition status...</p>
        </div>
      )}
      {hasData ? (
        <>
          {/* Competition Overview */}
          <div className="rl-competition-header">
            <div className="rl-metric-card">
              <h3>Total Agents</h3>
              <div className="rl-metric-value">{total_agents}</div>
            </div>

            <div className="rl-metric-card">
              <h3>Active Agents</h3>
              <div className="rl-metric-value" style={{ color: getStatusColor(active_agents, { good: 1, warning: 0 }) }}>
                {active_agents}
              </div>
            </div>

            <div className="rl-metric-card">
              <h3>Duration</h3>
              <div className="rl-metric-value">
                {duration_seconds ? `${Math.floor(duration_seconds / 60)}m ${Math.floor(duration_seconds % 60)}s` : '0s'}
              </div>
            </div>

            <div className="rl-metric-card">
              <h3>Status</h3>
              <div className={`rl-status ${status === 'running' ? 'running' : 'stopped'}`}>
                {status === 'running' ? '🟢 RUNNING' : '🔴 STOPPED'}
              </div>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="rl-competition-controls">
            <button
              onClick={handleOpenConfigModal}
              disabled={loading || status === 'running'}
              className="rl-start-competition-btn"
            >
              {loading ? '🚀 Starting...' : '🚀 Start Enhanced Competition'}
            </button>
          <div className="rl-inline-field">
            <select
              value={bestExperimentId || ''}
              onChange={(e) => setBestExperimentId(e.target.value)}
              disabled={loading || status === 'running'}
            >
              <option value="">Select experiment</option>
              {availableExperiments.map((exp) => (
                <option key={exp.experiment_id} value={exp.experiment_id}>
                  {exp.experiment_id} ({exp.completed_combinations || 0} combos)
                </option>
              ))}
            </select>
            <button
              onClick={handleStartBestModels}
              disabled={loading || status === 'running'}
              className="rl-start-competition-btn tertiary"
            >
              {loading ? '🏁 Starting...' : '🏁 Start Best Models'}
            </button>
          </div>
            <button
              onClick={handleStartCompetition}
              disabled={loading || status === 'running' || total_agents === 0}
              className="rl-start-competition-btn secondary"
            >
              {loading ? '▶️ Starting...' : '▶️ Start Competition'}
            </button>
            <button
              onClick={handleStopCompetition}
              disabled={loading || status !== 'running'}
              className="rl-stop-competition-btn"
            >
              {loading ? '⏹️ Stopping...' : '⏹️ Stop Competition'}
            </button>
            <button
              onClick={handleResetCompetition}
              disabled={loading}
              className="rl-reset-competition-btn"
            >
              {loading ? '🔄 Resetting...' : '🔄 Reset Competition'}
            </button>
          </div>
        </>
      ) : (
        <div className="rl-competition-empty">
          <div className="rl-trophy-icon">🏆</div>
          <p>No competition data available. Start a competition to see agents and leaderboards.</p>
          <div className="rl-empty-actions">
            <button
              onClick={handleOpenConfigModal}
              className="rl-start-competition-btn"
              disabled={loading}
            >
              🚀 Start Enhanced Competition
            </button>
            <div className="rl-inline-field">
              <select
                value={bestExperimentId || ''}
                onChange={(e) => setBestExperimentId(e.target.value)}
                disabled={loading}
              >
                <option value="">Select experiment</option>
                {availableExperiments.map((exp) => (
                  <option key={exp.experiment_id} value={exp.experiment_id}>
                    {exp.experiment_id} ({exp.completed_combinations || 0} combos)
                  </option>
                ))}
              </select>
              <button
                onClick={handleStartBestModels}
                className="rl-start-competition-btn secondary"
                disabled={loading}
              >
                🏁 Start Best Models
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard */}
      {displayLeaderboard.length > 0 && (
        <div className="rl-competition-section">
          <h3>🏆 Leaderboard</h3>
          <div className="rl-leaderboard-table">
            <div className="rl-table-header">
              <span>Rank</span>
              <span>Agent</span>
              <span>Portfolio Value</span>
              <span>Total Return</span>
              <span>Max Drawdown</span>
              <span>Total Trades</span>
              <span>Status</span>
            </div>
            {displayLeaderboard.map((agent, index) => (
              <div key={agent.agent_id} className="rl-table-row">
                <span className="rl-rank">{getRankIcon(agent.rank)}</span>
                <span>
                  <div className="rl-agent-name">{agent.name}</div>
                  <div className="rl-agent-id">{agent.agent_id}</div>
                </span>
                <span className="rl-portfolio-value">{formatCurrency(agent.portfolio_value)}</span>
                <span style={{ color: getStatusColor(agent.total_return_pct) }}>
                  {formatPercentage(agent.total_return_pct)}
                </span>
                <span style={{ color: '#f56565' }}>
                  {formatDrawdown(agent.max_drawdown, agent.portfolio_value)}
                </span>
                <span>{agent.total_trades}</span>
                <span className={`rl-agent-status ${agent_details[agent.agent_id]?.is_active ? 'active' : 'inactive'}`}>
                  {agent_details[agent.agent_id]?.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agent Details */}
      {agentEntries.length > 0 && (
        <div className="rl-competition-section">
          <h3>🤖 Agent Details</h3>
          <div className="rl-agent-details-grid">
            {agentEntries.map(([agentId, details]) => (
              <div key={agentId} className="rl-agent-card">
                <div className="rl-agent-header">
                  <h4>{details.name}</h4>
                  <span className={`rl-agent-status ${details.is_active ? 'active' : 'inactive'}`}>
                    {details.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="rl-agent-metrics">
                  <div className="rl-agent-metric">
                    <span className="rl-label">Portfolio:</span>
                    <span className="rl-value">{formatCurrency(details.portfolio_value)}</span>
                  </div>
                  <div className="rl-agent-metric">
                    <span className="rl-label">Return:</span>
                    <span className="rl-value" style={{ color: getStatusColor(details.total_return_pct) }}>
                      {formatPercentage(details.total_return_pct)}
                    </span>
                  </div>
                  <div className="rl-agent-metric">
                    <span className="rl-label">Trades:</span>
                    <span className="rl-value">{details.total_trades}</span>
                  </div>
                  <div className="rl-agent-metric">
                    <span className="rl-label">Drawdown:</span>
                    <span className="rl-value" style={{ color: '#f56565' }}>
                      {formatDrawdown(details.max_drawdown, details.portfolio_value)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agent Orders */}
      {Object.keys(agent_details).length > 0 && (
        <div className="rl-competition-section">
          <h3>📊 Agent Orders</h3>
          <div className="rl-agent-positions">
            {Object.entries(agent_details).map(([agentId, details]) => (
              <div key={agentId} className="rl-agent-positions-card">
                <div className="rl-agent-positions-header">
                  <h4>{details.name}</h4>
                  <div className="rl-agent-positions-summary">
                    <span className="rl-positions-count">
                      {details.trades?.length || 0} orders
                    </span>
                    <span className="rl-portfolio-value">
                      {formatCurrency(details.portfolio_value)}
                    </span>
                  </div>
                </div>
                {details.trades && details.trades.length > 0 ? (
                  <div className="rl-positions-table">
                    <div className="rl-positions-header">
                      <span>Symbol</span>
                      <span>Side</span>
                      <span>Amount</span>
                      <span>Price</span>
                      <span>Total $</span>
                      <span>P&L $</span>
                      <span>Timestamp</span>
                      <span>Trade ID</span>
                    </div>
                    {details.trades.map((trade, index) => {
                      const timestamp = new Date(trade.timestamp * 1000);
                      const totalValue = trade.amount * trade.price;

                      // For buy orders, show unrealized P&L if we have current position data
                      // For sell orders, show realized P&L (simplified as 0 for now since we don't have entry price)
                      let pnlValue = 0;
                      let pnlClass = '';

                      if (trade.side.toLowerCase() === 'buy') {
                        // Check if this symbol is in current positions to calculate unrealized P&L
                        const currentPosition = details.positions?.[trade.symbol];
                        if (currentPosition) {
                          // Calculate unrealized P&L for this trade's contribution
                          // This is simplified - in reality we'd need to track each trade's contribution
                          pnlValue = (currentPosition.current_price - trade.price) * trade.amount;
                          pnlClass = pnlValue >= 0 ? 'positive' : 'negative';
                        }
                      } else {
                        // For sell orders, P&L would be (sell_price - entry_price) * amount
                        // Since we don't have entry price tracking, show as neutral for now
                        pnlValue = 0;
                        pnlClass = 'neutral';
                      }

                      return (
                        <div key={`${trade.trade_id}-${index}`} className="rl-positions-row">
                          <span className="rl-symbol">{trade.symbol}</span>
                          <span className={`rl-side ${trade.side.toLowerCase()}`}>
                            {trade.side.toUpperCase()}
                          </span>
                          <span className="rl-amount">{trade.amount.toFixed(6)}</span>
                          <span className="rl-price">{formatCurrency(trade.price)}</span>
                          <span className="rl-total-value">{formatCurrency(totalValue)}</span>
                          <span className={`rl-pnl-value ${pnlClass}`}>
                            {formatCurrency(pnlValue)}
                          </span>
                          <span className="rl-timestamp">
                            {timestamp.toLocaleString()}
                          </span>
                          <span className="rl-trade-id">{trade.trade_id}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rl-no-positions">
                    <p>No orders executed yet</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Competitions */}
      {completed_competitions.length > 0 && (
        <div className="rl-competition-section">
          <h3>📚 Competition History</h3>
          <div className="rl-completed-competitions">
            {completed_competitions.slice(-5).reverse().map((comp, index) => (
              <div key={comp.competition_id || index} className="rl-completed-competition-card">
                <div className="rl-completed-header">
                  <h4>{comp.competition_id || `Competition ${index + 1}`}</h4>
                  <span className="rl-completed-status">COMPLETED</span>
                </div>
                <div className="rl-completed-metrics">
                  <div className="rl-completed-metric">
                    <span className="rl-label">Duration:</span>
                    <span className="rl-value">
                      {comp.duration_seconds ? `${Math.floor(comp.duration_seconds / 60)}m ${Math.floor(comp.duration_seconds % 60)}s` : 'N/A'}
                    </span>
                  </div>
                  <div className="rl-completed-metric">
                    <span className="rl-label">Agents:</span>
                    <span className="rl-value">{comp.total_agents || 0}</span>
                  </div>
                  <div className="rl-completed-metric">
                    <span className="rl-label">Best Return:</span>
                    <span className="rl-value" style={{ color: getStatusColor(comp.leaderboard?.[0]?.total_return_pct || 0) }}>
                      {comp.leaderboard?.[0] ? formatPercentage(comp.leaderboard[0].total_return_pct) : 'N/A'}
                    </span>
                  </div>
                  <div className="rl-completed-metric">
                    <span className="rl-label">Winner:</span>
                    <span className="rl-value">
                      {comp.leaderboard?.[0]?.name || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {leaderboard.length === 0 && completed_competitions.length === 0 && (
        <div className="rl-competition-empty">
          <div className="rl-trophy-icon">🏆</div>
          <p>No competition data available. Export the best models from combinatorial testing to start a competition.</p>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="rl-modal-overlay" onClick={() => setShowConfigModal(false)}>
          <div className="rl-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="rl-modal-header">
              <h3>🚀 Enhanced Competition Configuration</h3>
              <button
                className="rl-modal-close"
                onClick={() => setShowConfigModal(false)}
              >
                ×
              </button>
            </div>

            <div className="rl-modal-body">
              <div className="rl-config-section">
                <h4>Model Selection</h4>
                <div className="rl-config-grid">
                  <div className="rl-config-field">
                    <label>Experiment:</label>
                    <select
                      value={competitionConfig.experiment_id || ''}
                      onChange={(e) => setCompetitionConfig({
                        ...competitionConfig,
                        experiment_id: e.target.value || null
                      })}
                    >
                      <option value="">Auto-select best experiment</option>
                      {availableExperiments.map(exp => (
                        <option key={exp.experiment_id} value={exp.experiment_id}>
                          {exp.experiment_id} ({exp.completed_combinations || 0} combinations)
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="rl-config-field">
                    <label>Number of Models:</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={competitionConfig.num_models}
                      onChange={(e) => setCompetitionConfig({
                        ...competitionConfig,
                        num_models: parseInt(e.target.value) || 3
                      })}
                    />
                  </div>

                  <div className="rl-config-field">
                    <label>Selection Criteria:</label>
                    <select
                      value={competitionConfig.selection_criteria}
                      onChange={(e) => setCompetitionConfig({
                        ...competitionConfig,
                        selection_criteria: e.target.value
                      })}
                    >
                      <option value="sharpe_ratio">Sharpe Ratio</option>
                      <option value="win_rate">Win Rate</option>
                      <option value="total_return">Total Return</option>
                      <option value="combined_score">Combined Score</option>
                    </select>
                  </div>

                  <div className="rl-config-field">
                    <label>Ensure Diversity:</label>
                    <input
                      type="checkbox"
                      checked={competitionConfig.ensure_diversity}
                      onChange={(e) => setCompetitionConfig({
                        ...competitionConfig,
                        ensure_diversity: e.target.checked
                      })}
                    />
                  </div>
                </div>
              </div>

              <div className="rl-config-section">
                <h4>Competition Settings</h4>
                <div className="rl-config-grid">
                  <div className="rl-config-field">
                    <label>Max Duration (hours):</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="Unlimited"
                      value={competitionConfig.max_duration_hours || ''}
                      onChange={(e) => setCompetitionConfig({
                        ...competitionConfig,
                        max_duration_hours: e.target.value ? parseFloat(e.target.value) : null
                      })}
                    />
                  </div>

                  <div className="rl-config-field">
                    <label>Risk Multiplier:</label>
                    <input
                      type="number"
                      min="0.1"
                      max="5.0"
                      step="0.1"
                      value={competitionConfig.risk_multiplier}
                      onChange={(e) => setCompetitionConfig({
                        ...competitionConfig,
                        risk_multiplier: parseFloat(e.target.value) || 1.0
                      })}
                    />
                  </div>

                  <div className="rl-config-field">
                    <label>Allow Short Selling:</label>
                    <input
                      type="checkbox"
                      checked={competitionConfig.allow_short_selling}
                      onChange={(e) => setCompetitionConfig({
                        ...competitionConfig,
                        allow_short_selling: e.target.checked
                      })}
                    />
                  </div>

                  <div className="rl-config-field">
                    <label>Initial Balance ($):</label>
                    <input
                      type="number"
                      min="1000"
                      step="1000"
                      value={competitionConfig.initial_balance}
                      onChange={(e) => setCompetitionConfig({
                        ...competitionConfig,
                        initial_balance: parseFloat(e.target.value) || 10000
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Model Preview */}
              {modelPreview && (
                <div className="rl-config-section">
                  <h4>📊 Model Preview</h4>
                  <div className="rl-preview-grid">
                    {modelPreview.selected_models?.map((model, index) => (
                      <div key={model.model_id} className="rl-preview-card">
                        <div className="rl-preview-header">
                          <h5>Rank #{model.rank}</h5>
                          <span className="rl-preview-indicators">
                            {model.indicators?.join(' + ') || 'N/A'}
                          </span>
                        </div>
                        <div className="rl-preview-metrics">
                          <div className="rl-preview-metric">
                            <span>Sharpe:</span>
                            <span>{model.sharpe_ratio?.toFixed(2) || 'N/A'}</span>
                          </div>
                          <div className="rl-preview-metric">
                            <span>Win Rate:</span>
                            <span>{model.win_rate ? `${(model.win_rate * 100).toFixed(1)}%` : 'N/A'}</span>
                          </div>
                          <div className="rl-preview-metric">
                            <span>P&L:</span>
                            <span>${model.net_pnl?.toFixed(2) || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {modelPreview.fallback_mode && (
                    <div className="rl-warning-message">
                      ⚠️ Using fallback models - no combinatorial experiments available
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="rl-modal-footer">
              <button
                className="rl-btn-secondary"
                onClick={handlePreviewModels}
                disabled={loading}
              >
                {loading ? '🔄 Previewing...' : '🔍 Preview Models'}
              </button>
              <button
                className="rl-btn-primary"
                onClick={handleStartEnhancedCompetition}
                disabled={loading || !modelPreview}
              >
                {loading ? '🚀 Starting...' : '🚀 Start Enhanced Competition'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RLCompetition;